import React, { useEffect, useMemo, useRef, useState } from "react";

export interface SearchableOption {
  value: number | string;
  label: string;
}

interface Props {
  name: string;
  options: SearchableOption[];
  // react-hook-form (v6) `register`; the selected value is submitted through a hidden input.
  register?: any;
  required?: boolean;
  placeholder?: string;
  defaultValue?: number | string;
  isLoading?: boolean;
  onChange?: (value: number | string) => void;
}

const SearchableSelect: React.FC<Props> = ({
  name,
  options,
  register,
  required,
  placeholder = "Select...",
  defaultValue,
  isLoading,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<number | string | undefined>(defaultValue);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? options.filter((o) => o.label?.toLowerCase().includes(term)) : options;
  }, [options, search]);

  const selectedLabel = options.find((o) => String(o.value) === String(selected))?.label;

  useEffect(() => {
    if (!isOpen) return;
    searchRef.current?.focus();

    const onOutsideClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", onOutsideClick);
    return () => document.removeEventListener("mousedown", onOutsideClick);
  }, [isOpen]);

  const choose = (option: SearchableOption) => {
    setSelected(option.value);
    setIsOpen(false);
    setSearch("");
    onChange?.(option.value);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setIsOpen(false);
    }
    if (e.key === "Enter") {
      // Keep Enter from submitting the surrounding form; pick the first match instead.
      e.preventDefault();
      if (filtered.length > 0) choose(filtered[0]);
    }
  };

  return (
    <div className="searchable-select" ref={containerRef}>
      <input
        type="hidden"
        name={name}
        value={selected ?? ""}
        readOnly
        ref={register ? register({ required }) : undefined}
      />
      <button
        type="button"
        className={`form-control searchable-select-toggle ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className={selectedLabel ? "" : "searchable-select-placeholder"}>
          {selectedLabel ?? (isLoading ? "Loading..." : placeholder)}
        </span>
        <i className="fa fa-angle-down" />
      </button>
      {isOpen && (
        <div className="searchable-select-menu">
          <div className="searchable-select-search">
            <i className="fa fa-search" />
            <input
              ref={searchRef}
              type="text"
              className="form-control form-control-sm"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKeyDown}
            />
          </div>
          <ul className="searchable-select-options">
            {filtered.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  className={String(o.value) === String(selected) ? "is-selected" : ""}
                  onClick={() => choose(o)}
                >
                  {o.label}
                </button>
              </li>
            ))}
            {filtered.length === 0 && <li className="searchable-select-empty">No matches</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
